import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Headline,
  Modal,
  ModalBareboneProps,
  styled,
  ToastNotification,
} from '@zeiss/beyond-online-react';
import {
  Box,
  ModalButtonContainer,
  ModalContainer,
  ModalContentContainer,
} from '@zeiss/lib-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSendInvitation } from 'src/modules/Invitations/hooks/useSendInvitation';
import { GetInvitationResponse } from 'src/types/rest/managementPortalApi';

const StyledModal = styled(Modal)`
  /* Limit modal size on larger screens to something reasonable */
  max-width: 51rem;
`;

export type SendInvitationModalData = Pick<
  GetInvitationResponse,
  'code' | 'email' | 'sentCount'
>;

type SendInvitationModalProps = Pick<
  ModalBareboneProps,
  'isOpen' | 'onClose'
> & {
  data: SendInvitationModalData;
  onSuccess?: () => void;
};

export const SendInvitationModal = ({
  data: { code, email, sentCount },
  isOpen,
  onClose,
  onSuccess,
}: SendInvitationModalProps) => {
  const { t } = useTranslation(['common', 'invitations']);
  const queryClient = useQueryClient();

  const [confirmed, setConfirmed] = useState(false);
  const hasBeenSent = sentCount >= 1;

  const closeModal = () => {
    setConfirmed(false);
    onClose?.();
  };

  const { isPending, sendInvitation } = useSendInvitation({
    onSuccess: () => {
      closeModal();
      queryClient.invalidateQueries({
        queryKey: ['invitation', code],
      });
      onSuccess?.();
    },
  });

  useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
    }
  }, [isOpen]);

  const handleSendInvitation = () => {
    if (!code) return;
    sendInvitation(code);
  };

  const toggleCheckbox = () => setConfirmed((prevConfirmed) => !prevConfirmed);

  return (
    <StyledModal isOpen={isOpen} onClose={closeModal}>
      <ModalContainer>
        <ModalContentContainer>
          <Box mode="static" spacing="m">
            <Headline
              headline={
                hasBeenSent
                  ? t('invitations:title.resendInvitationTo')
                  : t('invitations:title.sendInvitationTo')
              }
              subHeadline={email}
            />
          </Box>

          <Box mode="static" spacing="m">
            <ToastNotification
              description={t('invitations:text.confirmation')}
              nonDismissible
              title={t('invitations:title.confirmation')}
              type="warning"
            />
          </Box>

          <Checkbox
            // TODO: Add `isRequired` prop when we migrate to beyond-online-react v5
            checked={confirmed}
            label={t('invitations:text.terms')}
            onClick={toggleCheckbox}
          />
        </ModalContentContainer>

        <ModalButtonContainer>
          <Button onClick={closeModal} size="l" variant="secondary">
            {t('common:action.cancel')}
          </Button>

          <Button
            disabled={!confirmed || !code || isPending}
            icon="SendMessage"
            loading={isPending}
            onClick={handleSendInvitation}
            size="l"
          >
            {hasBeenSent
              ? t('invitations:action.resendInvitation')
              : t('invitations:action.sendInvitation')}
          </Button>
        </ModalButtonContainer>
      </ModalContainer>
    </StyledModal>
  );
};
